import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Header';
import MobileMenu from '../MobileMenu';
import Footer from '../Footer';
import GameNavigationBar from './menu/GameNavigationBar';
import GameContainer from './GameContainer';
import ScoreSummary from './ScoreSummary';
import GameDetails from './GameDetails';
import ResetConfirmModal from './menu/ResetConfirmModal';
import ProgressBar from './menu/ProgressBar';
import StudentNameForm from './menu/StudentNameForm';
import GamePreviewBar from './menu/GamePreviewBar';
import apiClient from '../../api/apiClient';
import {
  getAccessToken,
  getRefreshToken,
  removeTokens,
  setAccessToken,
} from '../../api/authUtils';
import { jwtDecode } from 'jwt-decode';
import './styles/InteractiveGame.css';
import { IconButton, Slider, Box, Tooltip, Typography, Button, useMediaQuery, Fade, Paper, Snackbar, Alert, Chip } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MusicOffIcon from '@mui/icons-material/MusicOff';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';

// Componente per la schermata di completamento verifica
const VerificationComplete = ({ onBackToGames, gameData, completedGames, studentName }) => {
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const [showRetryButton, setShowRetryButton] = useState(false);
  
  // Funzione per pulire il localStorage al termine della verifica
  const cleanupLocalStorage = (gameIdFromUrl) => {
    try {
      // Rimuovi tutti i dati correlati alla verifica
      localStorage.removeItem(`game_results_${gameIdFromUrl}`);
      localStorage.removeItem(`student_name_${gameIdFromUrl}`);
      
      // Pulizia più aggressiva per trovare tutti i dati correlati
      Object.keys(localStorage).forEach(key => {
        // Verifica se la chiave è correlata a questa verifica
        if (key.includes(`${gameIdFromUrl}`) || 
            (completedGames && completedGames.some(game => key.includes(`${game.chatId}`)))) {
          localStorage.removeItem(key);
          console.log(`VerificationComplete: Rimosso dato localStorage: ${key}`);
        }
      });
      
      console.log('VerificationComplete: Dati locali rimossi con successo dal localStorage');
    } catch (e) {
      console.error('VerificationComplete: Errore durante la rimozione dei dati locali:', e);
    }
  };
  
  // Funzione di supporto per inviare i risultati
  const sendResults = async () => {
    // Non tentare un nuovo salvataggio se è già in corso
    if (savePending) {
      console.log('VerificationComplete: Salvataggio già in corso, ignoro la richiesta');
      return;
    }
    
    // Non tentare un nuovo salvataggio se è già stato completato con successo
    if (saveSuccess) {
      console.log('VerificationComplete: Salvataggio già completato con successo, ignoro la richiesta');
      return;
    }
    
    setSaveAttempted(true);
    setSavePending(true);
    setShowRetryButton(false);
    console.log('VerificationComplete: tentativo di salvataggio finale');
    
    try {
      // Recupera il gameId dall'URL
      const currentPath = window.location.pathname;
      const pathParts = currentPath.split('/');
      const gameIdFromUrl = pathParts[pathParts.indexOf('games') + 1];
      
      if (!gameIdFromUrl || !studentName || !completedGames || completedGames.length === 0) {
        console.error('Dati insufficienti per il salvataggio:', { 
          gameIdFromUrl, 
          hasStudentName: !!studentName, 
          completedGamesCount: completedGames?.length || 0 
        });
        setSaveSuccess(false);
        console.log('VerificationComplete: Dati insufficienti, setSaveSuccess(false)');
        // Mostreremo il pulsante di retry dopo una pausa
        setTimeout(() => {
          setShowRetryButton(true);
        }, 3000);
        return;
      }
      
      // Calcolo del punteggio totale (media dei punteggi numerici delle attività)
      const validScores = completedGames
        .filter(game => {
          // Escludiamo i tipi di gioco che non devono influire sul punteggio
          const skipTypes = ['scrittura', 'lettura', 'video'];
          if (skipTypes.includes(game.type)) {
            console.log(`VerificationComplete: Escludo dal calcolo del punteggio l'attività di tipo ${game.type}`, game.chatId);
            return false;
          }
          // Includiamo solo i giochi con punteggi numerici validi
          return typeof game.score === 'number' && !isNaN(game.score);
        })
        .map(game => game.score);
      
      console.log('VerificationComplete: Punteggi validi considerati per la media:', validScores);
      
      const totalScore = validScores.length > 0 
        ? Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) 
        : 0;
      
      console.log('VerificationComplete: Punteggio totale calcolato:', totalScore);
      
      // Prepara i dati da inviare
      const submissionData = {
        student_name: studentName,
        game_id: gameIdFromUrl,
        game_title: gameData?.title || 'Attività Interattiva',
        activity_type: gameData?.activity_type || 'verifica',
        total_score: totalScore,
        completed_at: new Date().toISOString(),
        activities: completedGames.map(game => {
          const chatData = gameData?.game_data?.chats?.find(chat => chat.id === game.chatId) || {};
          
          // Gestione speciale per i giochi di tipo rearrange
          if (game.type === 'rearrange') {
            console.log('VerificationComplete: Preparazione dati speciali per rearrange:', game.chatId);
            return {
              chat_id: game.chatId,
              activity_type: game.type,
              position: chatData.position !== undefined ? chatData.position : 0,
              title: chatData.title || `Attività ${game.chatId}`,
              score: game.score || 0,
              max_score: 100,
              completed: true,
              completed_at: game.completedAt || new Date().toISOString(),
              correctArrangements: game.correctArrangements || 0,
              totalExercises: game.totalExercises || 0,
              percentage: game.percentage || 0,
              details: {
                correctArrangements: game.correctArrangements || 0,
                totalExercises: game.totalExercises || 0,
                percentage: game.percentage || 0,
                completed: true,
                gameType: 'rearrange'
              },
              arrangementResults: game.arrangementResults || []
            };
          }
          
          return {
            chat_id: game.chatId,
            activity_type: game.type || 'unknown',
            position: chatData.position !== undefined ? chatData.position : 0,
            title: chatData.title || `Attività ${game.chatId}`,
            score: game.score || 0,
            max_score: 100,
            completed: game.completed || false,
            completed_at: game.completedAt || new Date().toISOString(),
            ...game  // Include tutti i dati specifici del gioco
          };
        })
      };
      
      console.log('VerificationComplete: invio dati:', submissionData);
      
      // Invia i dati al backend
      console.log('VerificationComplete: inizio richiesta API');
      const response = await apiClient.post('/api/advantage_games/games/submit-results/', submissionData);
      console.log('VerificationComplete: risposta API ricevuta:', response.status, response.data);
      
      console.log('VerificationComplete: Prima di setSaveSuccess - saveSuccess attuale:', saveSuccess);
      
      // Aggiorniamo esplicitamente lo stato di successo
      setSaveSuccess(true);
      
      console.log('VerificationComplete: Dopo setSaveSuccess(true) - chiamo pulizia dati');
      
      // Pulizia immediata dei dati dopo il salvataggio riuscito
      cleanupLocalStorage(gameIdFromUrl);
      
      // Pulizia ritardata per assicurarsi che tutto sia pulito anche dopo i redirect
      setTimeout(() => {
        cleanupLocalStorage(gameIdFromUrl);
        console.log('VerificationComplete: Pulizia ritardata eseguita');
      }, 2000);
      
      // Forziamo un aggiornamento dello stato dopo un breve ritardo per assicurarci che React lo registri
      setTimeout(() => {
        console.log('VerificationComplete: Verifica saveSuccess dopo timeout:', saveSuccess);
        // Riaffermo lo stato di successo per sicurezza
        setSaveSuccess(true);
      }, 500);
      
    } catch (error) {
      console.error('Errore durante il salvataggio finale:', error);
      // Impostiamo saveSuccess a false esplicitamente in caso di errore
      setSaveSuccess(false);
      console.log('VerificationComplete: Errore API, setSaveSuccess(false)');
      // Mostra il pulsante di retry dopo una pausa
      setTimeout(() => {
        setShowRetryButton(true);
      }, 3000);
      // Non rimuoviamo i dati in caso di errore per consentire tentativi successivi
    } finally {
      console.log('VerificationComplete: completamento operazione di salvataggio, savePending=false');
      // Sempre alla fine dell'operazione, aggiorniamo lo stato di savePending
      setSavePending(false);
    }
  };

  // Effettua il salvataggio quando il componente viene montato
  useEffect(() => {
    // Verifica la presenza di giochi di tipo rearrange
    if (completedGames && completedGames.length > 0) {
      const rearrangeGames = completedGames.filter(game => game.type === 'rearrange');
      console.log('VerificationComplete: Rilevati', rearrangeGames.length, 'giochi di tipo rearrange');
      if (rearrangeGames.length > 0) {
        console.log('VerificationComplete: Dettagli giochi rearrange:', rearrangeGames);
      }
    }
    
    // Inizia il processo di salvataggio
    sendResults();
    
    // Imposta un timer per verificare lo stato del salvataggio dopo un certo periodo
    const checkSaveStatusTimer = setTimeout(() => {
      if (savePending && !saveSuccess) {
        console.log('VerificationComplete: Controllo stato salvataggio dopo timeout');
        // Se è ancora in attesa dopo 15 secondi, mostra il pulsante di retry
        setShowRetryButton(true);
        setSavePending(false);
      }
    }, 15000); // 15 secondi
    
    // Al momento della dismissione del componente, effettua un'ultima pulizia e cancella i timer
    return () => {
      clearTimeout(checkSaveStatusTimer);
      try {
        // Recupera il gameId dall'URL
        const currentPath = window.location.pathname;
        const pathParts = currentPath.split('/');
        const gameIdFromUrl = pathParts[pathParts.indexOf('games') + 1];
        
        if (gameIdFromUrl) {
          console.log('VerificationComplete: Pulizia finale al momento della dismissione');
          cleanupLocalStorage(gameIdFromUrl);
        }
      } catch (e) {
        console.error('VerificationComplete: Errore durante la pulizia finale:', e);
      }
    };
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
      className="verification-complete-container"
    >
      {console.log('VerificationComplete: Rendering - saveAttempted:', saveAttempted, 'saveSuccess:', saveSuccess, 'savePending:', savePending, 'showRetryButton:', showRetryButton)}
      
      <EmojiEventsIcon sx={{ fontSize: 80, color: '#df4634', mb: 2 }} />
      
      <Typography variant="h4" component="h2" sx={{ 
        color: '#df4634', 
        fontWeight: 'bold',
        textAlign: 'center',
        fontFamily: '"Comic Sans MS", "Comic Sans", cursive',
        mb: 2
      }}>
        Hai completato la verifica!
      </Typography>
      
      <Typography variant="body1" sx={{ 
        textAlign: 'center', 
        mb: 3,
        color: 'white',
        fontSize: '1.1rem'
      }}>
        Complimenti per aver portato a termine tutte le attività. 
        Il tuo insegnante potrà visualizzare i risultati.
      </Typography>
      
      {savePending && (
        <Typography variant="body1" sx={{ 
          textAlign: 'center', 
          mt: 2,
          mb: 2,
          color: '#FFA726',
          fontWeight: 'bold',
          fontSize: '1.1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          padding: '8px 12px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}>
          <span 
            style={{
              display: 'inline-block',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              border: '3px solid #FFA726',
              borderTopColor: 'transparent',
              animation: 'spin 1s linear infinite'
            }}
          ></span>
          Salvataggio risultati in corso...
        </Typography>
      )}
      
      {showRetryButton && !saveSuccess && !savePending && (
        <Button 
          variant="contained" 
          color="primary"
          onClick={sendResults}
          sx={{ mt: 2 }}
        >
          Riprova invio risultati
        </Button>
      )}

      {saveAttempted && saveSuccess && !savePending && (
        <Typography variant="body1" sx={{ 
          textAlign: 'center', 
          mt: 2,
          mb: 2,
          color: '#4CAF50',
          fontWeight: 'bold',
          fontSize: '1.1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          padding: '8px 12px',
          borderRadius: '8px'
        }}>
          ✓ Risultati salvati con successo
        </Typography>
      )}

      <Button 
        variant="contained" 
        color="secondary"
        onClick={onBackToGames}
        sx={{ 
          mt: 3, 
          backgroundColor: '#4CAF50',
          color: 'white',
          '&:hover': {
            backgroundColor: '#388E3C',
          },
          fontWeight: 'bold',
          borderRadius: '8px',
          padding: '10px 20px'
        }}
        startIcon={<PlayArrowIcon sx={{ transform: 'rotate(180deg)' }} />}
      >
        Torna alla schermata iniziale
      </Button>
    </motion.div>
  );
};

// Componente principale della pagina
const InteractiveGamePage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  // Struttura per i giochi completati: Array di oggetti con struttura standardizzata
  const [completedGames, setCompletedGames] = useState([]);
  const [showScoreSummary, setShowScoreSummary] = useState(false);
  const [selectedGameDetails, setSelectedGameDetails] = useState(null);
  // Stato per il modal di conferma reset
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // Stato per il feedback di copia URL
  const [showShareFeedback, setShowShareFeedback] = useState(false);
  // Stato per il controllo audio
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [showAudioControls, setShowAudioControls] = useState(false);
  const audioRef = useRef(null);
  
  // Stato per la gestione del nome dello studente
  const [studentName, setStudentName] = useState('');
  const [nameSubmitted, setNameSubmitted] = useState(false);
  // Stato per la notifica del salvataggio dei risultati
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  // Stato per il salvataggio dei risultati
  const [resultsSaving, setResultsSaving] = useState(false);
  const [resultsSaved, setResultsSaved] = useState(false);
  
  // Nuovo stato per verificare se l'utente è proprietario del gioco
  const [isOwner, setIsOwner] = useState(false);
  
  // Media query per rilevare dispositivi mobili
  const isMobile = useMediaQuery('(max-width:768px)');

  const { gameId } = useParams();
  const navigate = useNavigate();

  // Inizializzazione e controllo audio
  useEffect(() => {
    // Creiamo un elemento audio
    audioRef.current = new Audio('/sound/Game_sound_verifiche.mp3');
    audioRef.current.loop = true;
    audioRef.current.volume = volume;
    
    // Riproduciamo l'audio quando il componente viene montato
    if (!isMuted) {
      audioRef.current.play().catch(error => {
        console.warn('Autoplay non consentito:', error);
      });
    }
    
    // Puliamo quando il componente viene smontato
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Aggiornamento del volume quando cambia
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);
  
  // Gestione mute/unmute
  useEffect(() => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(error => {
          console.warn('Riproduzione audio non consentita:', error);
        });
      }
    }
  }, [isMuted]);

  // Funzione per gestire il toggle del mute
  const handleToggleMute = () => {
    setIsMuted(!isMuted);
  };
  
  // Funzione per gestire il cambio del volume
  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
  };

  // Check token validity on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Ogni volta che cambia l'ID del gioco, resettiamo lo stato
      setCurrentGameIndex(0);
      setCompletedGames([]);
      setShowScoreSummary(false);
      setSelectedGameDetails(null);
      
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken) {
        try {
          const decodedAccessToken = jwtDecode(accessToken);
          const currentTime = Date.now() / 1000;

          if (decodedAccessToken.exp && decodedAccessToken.exp > currentTime) {
            // Access token ancora valido
            setIsLoggedIn(true);
          } else if (refreshToken) {
            // Access token scaduto, provo a rinnovarlo con il refresh token
            try {
              const response = await apiClient.post('/auth/refresh', { refreshToken });
              if (response.data && response.data.accessToken) {
                setAccessToken(response.data.accessToken);
                setIsLoggedIn(true);
              } else {
                removeTokens();
                setIsLoggedIn(false);
              }
            } catch (error) {
              console.error('Errore durante il refresh del token:', error);
              removeTokens();
              setIsLoggedIn(false);
            }
          } else {
            removeTokens();
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.error('Errore durante la verifica del token:', error);
          removeTokens();
          setIsLoggedIn(false);
        }
      } else {
        setIsLoggedIn(false);
      }

      // Recuperiamo i risultati salvati in localStorage
      try {
        const savedResults = localStorage.getItem(`game_results_${gameId}`);
        if (savedResults) {
          const parsedResults = JSON.parse(savedResults);
          console.log('Caricati risultati salvati da localStorage:', parsedResults);
          setCompletedGames(parsedResults);
        }
      } catch (e) {
        console.error('Errore nel caricamento da localStorage:', e);
      }

      // Carica i dati del gioco
      await fetchGameData();
    };

    checkAuthStatus();
    
    // Listener per la chiusura della pagina per pulire i dati di verifiche completate
    const handleBeforeUnload = () => {
      if (gameData && gameData.activity_type === "verifica" && resultsSaved) {
        try {
          localStorage.removeItem(`game_results_${gameId}`);
          localStorage.removeItem(`student_name_${gameId}`);
          console.log('Dati locali rimossi durante la chiusura della pagina');
        } catch (e) {
          console.error('Errore durante la rimozione dei dati locali (chiusura):', e);
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [gameId, resultsSaved]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const fetchGameData = async () => {
    if (!gameId) {
      setError("ID del gioco mancante");
      setLoading(false);
      return;
    }

    try {
      console.log("Inizio caricamento dei dati del gioco:", gameId);
      // URL corretto identificato dai test
      const response = await apiClient.get(`/api/advantage_games/games/${gameId}/`);
      if (response.data) {
        setGameData(response.data);
        console.log("Dati del gioco caricati con successo:", response.data);
        
        // Verifichiamo se l'utente è proprietario del gioco 
        // usando l'endpoint specifico che funziona solo per i proprietari
        const accessToken = getAccessToken();
        if (accessToken) {
          console.log("Token di accesso trovato, verifico proprietà del gioco");
          try {
            // Se questa chiamata ha successo, l'utente è il proprietario
            const ownerResponse = await apiClient.get(`/api/advantage_games/games/${gameId}/globalchat/`);
            console.log("Risposta verifica proprietà:", ownerResponse);
            setIsOwner(true);
            console.log("L'utente è proprietario del gioco");
          } catch (ownerError) {
            // Se la chiamata fallisce, l'utente non è il proprietario
            console.log("L'utente non è il proprietario del gioco:", ownerError);
            setIsOwner(false);
          }
        } else {
          console.log("Utente non autenticato, impossibile verificare proprietà");
          setIsOwner(false);
        }
      } else {
        setError("Nessun dato ricevuto dal server");
      }
    } catch (error) {
      console.error("Errore durante il caricamento del gioco:", error);
      setError(`Errore durante il caricamento del gioco: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per gestire l'invio del nome dello studente
  const handleNameSubmit = (name) => {
    setStudentName(name);
    setNameSubmitted(true);
    
    // Salva il nome nel localStorage per comodità
    try {
      localStorage.setItem(`student_name_${gameId}`, name);
    } catch (e) {
      console.error('Errore nel salvataggio del nome in localStorage:', e);
    }
  };

  // Funzione per mostrare la notifica
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Funzione per chiudere la notifica
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Funzione per verificare se tutti i giochi sono stati completati
  // e in quel caso salva automaticamente i risultati
  const checkAndSaveResults = (games) => {
    if (!gameData || !gameData.game_data || !gameData.game_data.chats) return;
    
    // Non facciamo più questa funzione per evitare salvataggi duplicati
    // Il salvataggio automatico è ora gestito direttamente in handleGameComplete
    console.log('checkAndSaveResults: funzione deprecata, il salvataggio è ora gestito da handleGameComplete');
  };

  // Funzione migliorata per la pulizia completa del localStorage
  const cleanLocalStorageByGameId = (specificGameId = null) => {
    const idToClean = specificGameId || gameId;
    if (!idToClean) return;
    
    try {
      console.log(`Pulizia localStorage per gameId ${idToClean}`);
      
      // Rimuovi i risultati generali del gioco
      localStorage.removeItem(`game_results_${idToClean}`);
      localStorage.removeItem(`student_name_${idToClean}`);
      
      // Pulizia dei dati specifici per attività
      if (gameData && gameData.game_data && gameData.game_data.chats) {
        gameData.game_data.chats.forEach(chat => {
          // Pulisci dati per SelectGroup
          localStorage.removeItem(`selectgroup_partial_answers_${chat.id}`);
          localStorage.removeItem(`selectgroup_partial_answers_${chat.id}_meta`);
          
          // Pulisci dati per RearrangeGame
          localStorage.removeItem(`rearrange_partial_answers_${chat.id}`);
          localStorage.removeItem(`rearrange_partial_answers_${chat.id}_meta`);

          // Pulisci dati per QuizGame
          localStorage.removeItem(`quiz_partial_answers_${chat.id}`);
          
          // Pulisci dati per MatchPairsGame 
          localStorage.removeItem(`matchpairs_partial_answers_${chat.id}`);
          
          // Pulisci dati per FillTheBoxGame
          localStorage.removeItem(`fillthebox_partial_answers_${chat.id}`);
        });
      }
      
      // Metodo alternativo: cerca e rimuovi tutte le chiavi che contengono l'ID del gioco
      Object.keys(localStorage).forEach(key => {
        // Verifica se la chiave è correlata a questo gioco
        if (key.includes(`${idToClean}`) || 
            (gameData && gameData.game_data && gameData.game_data.chats && 
             gameData.game_data.chats.some(chat => key.includes(`${chat.id}`)))) {
          localStorage.removeItem(key);
          console.log(`Rimosso dato localStorage: ${key}`);
        }
      });
      
      // Esegui una seconda pulizia dopo un delay per assicurarti che tutto sia pulito
      setTimeout(() => {
        try {
          // Controlla se restano ancora dati
          const keysToClean = [];
          Object.keys(localStorage).forEach(key => {
            if (key.includes(`${idToClean}`) || 
                (gameData && gameData.game_data && gameData.game_data.chats && 
                 gameData.game_data.chats.some(chat => key.includes(`${chat.id}`)))) {
              keysToClean.push(key);
            }
          });
          
          // Se ci sono ancora chiavi da pulire, rimuovile
          if (keysToClean.length > 0) {
            console.log(`Trovate ${keysToClean.length} chiavi da rimuovere nel secondo passaggio:`, keysToClean);
            keysToClean.forEach(key => {
              localStorage.removeItem(key);
              console.log(`Rimosso dato localStorage (secondo passaggio): ${key}`);
            });
          } else {
            console.log('Secondo passaggio di pulizia: nessuna chiave da rimuovere');
          }
        } catch (e) {
          console.error('Errore nel secondo passaggio di pulizia localStorage:', e);
        }
      }, 1500);
    } catch (e) {
      console.error('Errore nella pulizia completa localStorage:', e);
    }
  };
  
  // Funzione per il salvataggio finale dei risultati
  const saveGameResults = () => {
    if (resultsSaved || resultsSaving) {
      console.log('saveGameResults: Risultati già salvati o in fase di salvataggio, esco');
      return; // Evita di salvare più volte
    }
    
    // Utilizza la funzione principale con i dati dallo stato completedGames
    saveGameResultsWithData(completedGames);
  };

  // Funzione per il salvataggio finale dei risultati con dati specifici
  const saveGameResultsWithData = async (gamesData) => {
    // Aggiungiamo un controllo con debounce per evitare salvataggi duplicati ravvicinati
    if (resultsSaved || resultsSaving) {
      console.log('saveGameResultsWithData: Risultati già salvati o in fase di salvataggio, esco');
      return; // Evita di salvare più volte
    }
    
    // Impostiamo subito resultsSaving a true per evitare chiamate multiple
    setResultsSaving(true);
    
    if (!studentName || studentName.trim() === "" || studentName === "Anonimo") {
      console.log('saveGameResultsWithData: Nome studente mancante o anonimo', { studentName });
      showSnackbar('Nome studente mancante o anonimo. Impossibile salvare i risultati.', 'error');
      setResultsSaving(false);
      return;
    }
    
    // Log dettagliato dei dati di gioco
    console.log('saveGameResultsWithData: Verifica dati giochi', { 
      gamesDataLength: gamesData?.length || 0,
      gamesData,
      gameDataChatsLength: gameData?.game_data?.chats?.length || 0
    });
    
    if (!gameData || !gamesData) {
      console.log('saveGameResultsWithData: gameData o gamesData null', { gameData: !!gameData, gamesData: !!gamesData });
      showSnackbar('Dati di gioco incompleti. Completa almeno un\'attività.', 'error');
      setResultsSaving(false);
      return;
    }
    
    if (gamesData.length === 0) {
      console.log('saveGameResultsWithData: gamesData è vuoto');
      showSnackbar('Dati di gioco incompleti. Completa almeno un\'attività.', 'error');
      setResultsSaving(false);
      return;
    }
    
    // Verifica che almeno un gioco sia stato effettivamente completato
    const validCompletedGames = gamesData.filter(game => game.completed === true);
    console.log('saveGameResultsWithData: Giochi completati validi', { 
      validCompletedGames: validCompletedGames.length,
      games: validCompletedGames.map(g => ({ chatId: g.chatId, type: g.type, completed: g.completed }))
    });
    
    if (validCompletedGames.length === 0) {
      console.log('saveGameResultsWithData: Nessun gioco risulta marcato come completato');
      showSnackbar('Dati di gioco incompleti. Completa almeno un\'attività.', 'error');
      setResultsSaving(false);
      return;
    }
    
    // Imposta un timeout di sicurezza per ripristinare lo stato di salvataggio in caso di errori imprevisti
    const safetyTimeout = setTimeout(() => {
      if (resultsSaving) {
        console.log('Timeout di sicurezza: ripristino dello stato di salvataggio dopo 30 secondi');
        setResultsSaving(false);
      }
    }, 30000); // 30 secondi di timeout
    
    try {
      // Calcolo del punteggio totale (media dei punteggi numerici delle attività)
      // Escludiamo le attività di tipo 'scrittura', 'lettura' e 'video' dal calcolo della media
      const validScores = gamesData
        .filter(game => {
          // Escludiamo i tipi di gioco che non devono influire sul punteggio
          const skipTypes = ['scrittura', 'lettura', 'video'];
          if (skipTypes.includes(game.type)) {
            console.log(`Escludo dal calcolo del punteggio l'attività di tipo ${game.type}`, game.chatId);
            return false;
          }
          // Includiamo solo i giochi con punteggi numerici validi
          return typeof game.score === 'number' && !isNaN(game.score);
        })
        .map(game => game.score);
      
      console.log('Punteggi validi considerati per la media:', validScores);
      
      const totalScore = validScores.length > 0 
        ? Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) 
        : 0;
      
      console.log('Punteggio totale calcolato:', totalScore);
      
      // Prepara i dati da inviare
      const submissionData = {
        student_name: studentName,
        game_id: gameId,
        game_title: gameData.title || 'Attività Interattiva',
        activity_type: gameData.activity_type || 'verifica',
        total_score: totalScore,
        completed_at: new Date().toISOString(),
        activities: gamesData.map(game => {
          const chatData = gameData.game_data.chats.find(chat => chat.id === game.chatId) || {};
          
          // Creiamo una copia profonda per evitare problemi di riferimento
          let gameCopy = {
            chat_id: game.chatId,
            activity_type: game.type || 'unknown',
            position: chatData.position !== undefined ? chatData.position : 0,
            title: chatData.title || `Attività ${game.chatId}`,
            score: game.score || 0,
            max_score: 100,
            completed: game.completed || false,
            completed_at: game.completedAt || new Date().toISOString()
          };
          
          // Aggiungiamo i campi specifici del tipo di gioco
          switch (game.type) {
            case 'rearrange':
              // Per rearrange, assicuriamoci che i campi specifici siano direttamente a livello principale
              gameCopy = {
                ...gameCopy,
                type: game.type,
                chatId: game.chatId,
                uniqueId: game.uniqueId,
                completedAt: game.completedAt,
                goToSummary: game.goToSummary || false,
                correctAnswers: game.correctAnswers || 0,
                totalQuestions: game.totalQuestions || 0,
                correctArrangements: game.correctArrangements || (game.details?.correctArrangements || 0),
                totalExercises: game.totalExercises || (game.details?.totalExercises || 0),
                percentage: game.percentage || (game.details?.percentage || 0),
                allAnswered: game.allAnswered || (game.details?.allAnswered || true),
                details: {
                  ...(game.details || {}),
                  correctArrangements: game.correctArrangements || (game.details?.correctArrangements || 0),
                  totalExercises: game.totalExercises || (game.details?.totalExercises || 0),
                  percentage: game.percentage || (game.details?.percentage || 0),
                  allAnswered: game.allAnswered || (game.details?.allAnswered || true)
                },
                arrangementResults: game.arrangementResults || []
              };
              break;
            case 'selectgroup':
              // Per selectgroup, assicuriamoci che tutti i campi siano presenti e formattati correttamente
              gameCopy = {
                ...gameCopy,
                type: game.type,
                chatId: game.chatId,
                uniqueId: game.uniqueId || `selectgroup_${game.chatId}_${Date.now()}`,
                completedAt: game.completedAt,
                goToSummary: game.goToSummary || false,
                score: typeof game.score === 'number' ? game.score : Number(game.score) || 0,
                correctAnswers: game.correctAnswers || 0,
                totalQuestions: game.totalQuestions || 0,
                totalWords: game.totalWords || 0,
                details: {
                  ...(game.details || {}),
                  gameType: 'selectgroup',
                  correctWords: game.details?.correctWords || game.correctAnswers || 0,
                  totalWords: game.details?.totalWords || game.totalQuestions || 0,
                  percentage: game.details?.percentage || game.score || 0,
                  placedWords: game.details?.placedWords || game.totalWords || 0,
                  completed: true,
                  completedAt: game.details?.completedAt || game.completedAt || new Date().toISOString()
                },
                // Assicuriamoci che groupResults sia sempre presente
                groupResults: game.groupResults || []
              };
              break;
            default:
              // Per gli altri tipi di gioco manteniamo la struttura attuale
              gameCopy = {
                ...gameCopy,
                ...game
              };
          }
          
          return gameCopy;
        })
      };
      
      // Log dettagliato del payload prima dell'invio
      console.log('Payload submission:', JSON.stringify(submissionData, null, 2));
      
      // Invia i dati al backend
      const response = await apiClient.post('/api/advantage_games/games/submit-results/', submissionData);
      
      console.log('Risultati salvati con successo:', response.data);
      setResultsSaved(true);
      showSnackbar('Risultati salvati con successo!', 'success');
      
      // PUNTO CRITICO: Pulizia aggressiva del localStorage per tutti i tipi di gioco
      console.log('Avvio pulizia aggressiva di tutto il localStorage correlato al gioco');
      cleanLocalStorageByGameId();
      
      // Esegui una seconda pulizia dopo un delay più lungo per sicurezza
      setTimeout(() => {
        console.log('Avvio seconda pulizia di sicurezza del localStorage');
        cleanLocalStorageByGameId();
      }, 2000);
      
    } catch (error) {
      console.error('Errore durante il salvataggio dei risultati:', error);
      showSnackbar('Errore durante il salvataggio dei risultati. Riprova.', 'error');
    } finally {
      clearTimeout(safetyTimeout);
      setResultsSaving(false);
    }
  };

  // Funzione che gestisce il completamento di un gioco
  const handleGameComplete = (result) => {
    // Log completo dei dati ricevuti
    console.log('===== GIOCO COMPLETATO =====');
    console.log('Dati ricevuti dal gioco:', result);
    console.log('Tipo di punteggio ricevuto:', typeof result.score, 'Valore:', result.score);
    
    if (!result || !result.chatId) {
      console.error('ERRORE: Dati mancanti o chatId mancante!', result);
      return;
    }
    
    // Assicuriamoci che il punteggio sia un numero valido
    let numericScore = 0;
    if (result.score !== undefined) {
      numericScore = Number(result.score);
      if (isNaN(numericScore)) {
        console.error('ERRORE: Il punteggio non è un numero valido!', result.score);
        numericScore = 0;
      }
    } else if (result.type === 'scrittura') {
      numericScore = 100;
    }
    
    // Creiamo una struttura standardizzata per tutti i tipi di gioco
    const standardizedResult = {
      // Metadati comuni a tutti i giochi
      type: result.type || 'unknown',
      chatId: result.chatId,
      uniqueId: `${result.type}_${result.chatId}_${Date.now()}`,
      completedAt: new Date().toISOString(),
      completed: true,
      
      // Punteggio (convertito sempre a numero)
      score: numericScore,
      
      // Conserviamo la proprietà goToSummary se presente
      goToSummary: result.goToSummary || false,
      
      // Statistiche specifiche per quiz e giochi con domande
      correctAnswers: result.correctAnswers !== undefined ? Number(result.correctAnswers) : undefined,
      totalQuestions: result.totalQuestions !== undefined ? Number(result.totalQuestions) : undefined,
      
      // Risultati dettagliati, specifici per tipo di gioco
      details: {
        ...(result.details || {}),
        gameType: result.type,
        completedAt: new Date().toISOString()
      }
    };
    
    // Aggiungiamo dettagli specifici per tipo di gioco
    switch (result.type) {
      case 'quiz':
        // Ci assicuriamo di includere i risultati dettagliati delle domande
        standardizedResult.questionResults = result.questionResults || [];
        break;
      case 'matchpairs':
        standardizedResult.pairResults = result.pairResults || [];
        break;
      case 'rearrange':
        standardizedResult.arrangementResults = result.arrangementResults || [];
        // Assicuriamoci che i dettagli specifici del rearrange siano correttamente mappati
        // e inclusi direttamente nell'oggetto principale
        if (result.details && typeof result.details === 'object') {
          // Incorpora i dettagli direttamente nell'oggetto principale
          standardizedResult.correctArrangements = result.details.correctArrangements || 0;
          standardizedResult.totalExercises = result.details.totalExercises || 0;
          standardizedResult.percentage = result.details.percentage || 0;
          standardizedResult.allAnswered = result.details.allAnswered || false;
          
          // Mantieni anche i dettagli nell'oggetto details
          standardizedResult.details = {
            ...result.details,
            gameType: result.type,
            completedAt: new Date().toISOString()
          };
        } else {
          // Crea dati di default se i dettagli sono mancanti
          const arrangementResults = standardizedResult.arrangementResults || [];
          const correctArrangements = arrangementResults.filter(a => a.isCorrect).length;
          const totalExercises = arrangementResults.length;
          const percentage = totalExercises > 0 ? Math.round((correctArrangements / totalExercises) * 100) : 0;
          
          standardizedResult.correctArrangements = correctArrangements;
          standardizedResult.totalExercises = totalExercises;
          standardizedResult.percentage = percentage;
          standardizedResult.allAnswered = true;
          
          standardizedResult.details = {
            correctArrangements,
            totalExercises,
            percentage,
            completed: true,
            allAnswered: true,
            gameType: result.type,
            completedAt: new Date().toISOString()
          };
        }
        break;
      case 'crossword':
        standardizedResult.wordResults = result.wordResults || [];
        // Assicuriamoci che il punteggio sia corretto nel caso del cruciverba
        if (result.score !== undefined) {
          standardizedResult.score = Number(result.score);
        }
        break;
      case 'selectgroup':
        standardizedResult.groupResults = result.groupResults || [];
        break;
      case 'fillthebox':
        standardizedResult.results = result.results || [];
        break;
      case 'scrittura':
        // Per i contenuti di testo/video, aggiungiamo info sul tipo
        standardizedResult.contentType = result.contentType || 'text';
        break;
    }
    
    // Cerchiamo se esiste già un risultato per questo chatId
    const existingIndex = completedGames.findIndex(game => game.chatId === standardizedResult.chatId);
    
    // Creiamo una nuova copia dell'array per mantenere l'immutabilità
    let newCompletedGames;
    
    if (existingIndex !== -1) {
      // Aggiorniamo il risultato esistente
      newCompletedGames = [...completedGames];
      newCompletedGames[existingIndex] = standardizedResult;
    } else {
      // Aggiungiamo un nuovo risultato
      newCompletedGames = [...completedGames, standardizedResult];
    }
    
    // Salviamo i risultati aggiornati nello stato
    setCompletedGames(newCompletedGames);
    
    // Salviamo i risultati anche in localStorage per persistenza
    try {
      // Verifichiamo che stiamo salvando dati validi
      if (!Array.isArray(newCompletedGames)) {
        console.error('ERRORE: newCompletedGames non è un array!', newCompletedGames);
        return;
      }
      
      // Verifichiamo che ogni gioco abbia un punteggio numerico valido
      newCompletedGames.forEach((game, index) => {
        if (typeof game.score !== 'number') {
          console.warn(`Gioco all'indice ${index} ha un punteggio non numerico:`, game.score);
          game.score = Number(game.score) || 0;
        }
      });
      
      const serializedGames = JSON.stringify(newCompletedGames);
      localStorage.setItem(`game_results_${gameId}`, serializedGames);
      
      // Se SelectGroup è stato completato, cancelliamo i suoi dati dal localStorage
      if (result.type === 'selectgroup') {
        localStorage.removeItem(`selectgroup_partial_answers_${result.chatId}`);
        localStorage.removeItem(`selectgroup_partial_answers_${result.chatId}_meta`);
        console.log(`Pulizia specifica dopo completamento SelectGroup: ${result.chatId}`);
      }
      // Se RearrangeGame è stato completato, cancelliamo i suoi dati dal localStorage
      else if (result.type === 'rearrange') {
        localStorage.removeItem(`rearrange_partial_answers_${result.chatId}`);
        localStorage.removeItem(`rearrange_partial_answers_${result.chatId}_meta`);
        console.log(`Pulizia specifica dopo completamento RearrangeGame: ${result.chatId}`);
      }
      
      // Verifichiamo che il salvataggio sia avvenuto correttamente
      const savedData = localStorage.getItem(`game_results_${gameId}`);
      if (!savedData) {
        console.error('ERRORE: Dati non trovati in localStorage dopo il salvataggio!');
      }
    } catch (e) {
      console.error('Errore nel salvataggio in localStorage:', e);
    }
    
    // Controlliamo se questo gioco ha la proprietà goToSummary impostata a true
    // e in tal caso passiamo alla schermata di riepilogo
    if (standardizedResult.goToSummary) {
      console.log('Gioco richiede di andare al riepilogo, navigo al riepilogo');
      setShowScoreSummary(true);
      return; // Usciamo dalla funzione per evitare di passare al gioco successivo
    }
    
    // Definiamo una funzione per controllare se dobbiamo salvare in base al tipo di attività
    const shouldSaveAutomatically = () => {
      // Verifica se l'attività è di tipo "verifica" e se c'è un nome studente valido
      if (gameData.activity_type === "verifica" && 
          studentName && studentName.trim() !== "" && studentName !== "Anonimo") {
        
        // Se c'è un solo gioco, verifico che sia stato completato
        if (gameData.game_data.chats.length === 1) {
          return newCompletedGames.some(game => game.chatId === gameData.game_data.chats[0].id);
        }
        
        // Altrimenti verifico che tutti i giochi siano completati
        return gameData.game_data.chats.every(chat => 
          newCompletedGames.some(game => game.chatId === chat.id)
        );
      }
      
      return false;
    };
    
    // Chiamiamo la funzione per verificare se abbiamo completato tutti i giochi
    // e in quel caso facciamo il salvataggio automatico dei risultati SOLO per verifiche
    // e SOLO SE c'è un nome studente valido (non vuoto e non "Anonimo")
    const needsAutomaticSave = shouldSaveAutomatically();
    console.log('È necessario salvare automaticamente?', needsAutomaticSave);
    
    if (needsAutomaticSave) {
      console.log('Attività di tipo verifica completata, salvataggio automatico attivato');
      
      // Chiamare in modo sicuro saveGameResultsWithData per evitare salvataggi duplicati
      if (!resultsSaved && !resultsSaving) {
        console.log('Avvio salvataggio automatico per verifica completata');
        saveGameResultsWithData(newCompletedGames);
      } else {
        console.log('Salvataggio già in corso o già completato, salto il salvataggio automatico');
      }
    } else {
      console.log('Non è necessario il salvataggio automatico in questo momento');
    }
    
    // Passaggio automatico al gioco successivo
    // Troviamo l'indice del gioco corrente utilizzando il chatId
    const currentChatIndex = gameData.game_data.chats.findIndex(chat => chat.id === result.chatId);
    
    if (currentChatIndex !== -1 && currentChatIndex < gameData.game_data.chats.length - 1) {
      // Se non è l'ultimo gioco, passa al successivo
      console.log(`Passaggio automatico dal gioco ${currentChatIndex + 1} al gioco ${currentChatIndex + 2}`);
      setTimeout(() => {
        setCurrentGameIndex(currentChatIndex + 1);
      }, 300); // Piccolo ritardo per garantire una transizione fluida
    } else if (currentChatIndex === gameData.game_data.chats.length - 1) {
      // Se è l'ultimo gioco, mostra il riepilogo
      console.log('Ultimo gioco completato, navigazione al riepilogo');
      
      // Per le verifiche, ci affidiamo al componente VerificationComplete per il salvataggio finale
      if (gameData.activity_type === "verifica") {
        console.log('Verifica completata, passaggio alla schermata di completamento (il salvataggio verrà gestito da VerificationComplete)');
        
        // Solo se il nome è valido, passiamo alla schermata di completamento
        if (studentName && studentName.trim() !== "" && studentName !== "Anonimo") {
          setTimeout(() => {
            setShowScoreSummary(true);
          }, 500);
        } else {
          console.warn('Nome studente non valido:', studentName);
          showSnackbar('Impossibile completare la verifica: nome studente non valido.', 'error');
        }
      } else {
        // Per esercitazioni, mostriamo subito il riepilogo
        setTimeout(() => {
          setShowScoreSummary(true);
        }, 300);
      }
    }
  };

  // Funzione per gestire la consegna finale
  const handleSubmitFinalResults = () => {
    // Verifichiamo se tutti i giochi sono stati completati
    const allGamesCompleted = gameData.game_data.chats.every(chat => 
      completedGames.some(game => game.chatId === chat.id)
    );
    
    if (allGamesCompleted) {
      // Per le verifiche, passiamo direttamente al componente VerificationComplete
      // che gestirà il salvataggio
      console.log('Giochi completati, passaggio al riepilogo');
      setShowScoreSummary(true);
      setSelectedGameDetails(null);
    } else {
      // Altrimenti troviamo il primo gioco non completato e ci spostiamo lì
      const firstIncompleteGameIndex = gameData.game_data.chats.findIndex(chat => 
        !completedGames.some(game => game.chatId === chat.id)
      );
      
      if (firstIncompleteGameIndex !== -1) {
        console.log("Reindirizzamento al primo gioco non completato:", firstIncompleteGameIndex);
        setCurrentGameIndex(firstIncompleteGameIndex);
      }
    }
  };

  // Funzione per navigare tra i giochi
  const handleNavigateToGame = (index) => {
    setCurrentGameIndex(index);
    setShowScoreSummary(false);
    setSelectedGameDetails(null);
  };

  // Funzione per tornare alla schermata dei giochi
  const handleBackToGames = () => {
    setShowScoreSummary(false);
    setSelectedGameDetails(null);
    setNameSubmitted(false); // Reset dello stato per tornare alla schermata di inserimento del nome
    
    // Pulizia dati per verifiche quando torniamo indietro dalla schermata di completamento
    if (gameData && gameData.activity_type === "verifica") {
      cleanLocalStorageByGameId();
      console.log('Dati locali rimossi durante la navigazione indietro dalla verifica completata');
    }
  };

  // Funzione per mostrare i dettagli di un gioco specifico
  const handleShowGameDetails = (gameResult, chat) => {
    // Se il gioco non è stato completato, crea un oggetto risultato vuoto
    if (!gameResult) {
      gameResult = {
        chatId: chat.id,
        game_type: chat.agent,
        score: 0,
        correctAnswers: 0,
        totalQuestions: 0,
        completed_at: new Date().toISOString(),
        wordResults: []
      };
    }
    
    setSelectedGameDetails({ gameResult, chat });
  };

  // Funzione per tornare al riepilogo generale
  const handleBackToSummary = () => {
    setSelectedGameDetails(null);
  };

  // Funzione per resettare completamente il gioco
  const handleResetGame = () => {
    // Mostra il modal di conferma
    setShowResetConfirm(true);
  };
  
  // Funzione per eseguire effettivamente il reset dopo la conferma
  const confirmReset = () => {
    // Cancella i punteggi salvati
    setCompletedGames([]);
    
    // Pulizia completa dei dati dal localStorage
    cleanLocalStorageByGameId();
    console.log('Dati locali rimossi durante il reset del gioco');
    
    // Torna alla schermata dei giochi e imposta il primo gioco
    setShowScoreSummary(false);
    setSelectedGameDetails(null);
    setCurrentGameIndex(0);
    
    // Chiudi il modal
    setShowResetConfirm(false);
  };
  
  // Funzione per annullare il reset
  const cancelReset = () => {
    setShowResetConfirm(false);
  };

  // Funzione per copiare l'URL corrente negli appunti
  const handleShareGame = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        setShowShareFeedback(true);
        setTimeout(() => {
          setShowShareFeedback(false);
        }, 2000);
      })
      .catch((error) => {
        console.error('Errore durante la copia dell\'URL:', error);
      });
  };

  // Funzione per navigare alla pagina dei risultati
  const handleViewResults = () => {
    navigate(`/games/${gameId}/results`);
  };

  return (
    <div className="interactive-game-page">
      <Header isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      {mobileMenuOpen && <MobileMenu isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} setMobileMenuOpen={setMobileMenuOpen} />}
      
      <main className="game-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="game-container"
        >
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Caricamento in corso...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <h2>Si è verificato un errore</h2>
              <p>{error}</p>
              <button onClick={() => navigate('/')}>Torna alla Home</button>
            </div>
          ) : gameData ? (
            !nameSubmitted ? (
              <>
                <div className="game-header">
                  <h1>{gameData.title || "Gioco Interattivo"}</h1>
                  <Chip 
                    icon={<AssignmentIcon />} 
                    label={gameData.activity_type === "verifica" ? "Verifica" : "Esercitazione"} 
                    color={gameData.activity_type === "verifica" ? "error" : "primary"}
                    className="activity-tag"
                    sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '0.9rem'
                    }} 
                  />
                </div>
                
                <div className="game-header-buttons">
                  <button 
                    className="share-button" 
                    onClick={handleShareGame}
                    aria-label="Condividi"
                    title="Condividi"
                  >
                    <img 
                      src="/interactivegame/share.png" 
                      alt="Condividi" 
                      className="share-icon" 
                    />
                    <span className="share-text">Condividi</span>
                    {showShareFeedback && <span className="share-feedback">URL copiato!</span>}
                  </button>
                </div>
                
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  borderRadius: 1, 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)' 
                }}>
                  <Typography variant="body1" sx={{ 
                    color: 'white'
                  }}>
                    {gameData.description || "Nessuna descrizione disponibile."}
                  </Typography>
                </Box>
                
                {/* Anteprima delle attività incluse nel gioco */}
                <GamePreviewBar chats={gameData.game_data.chats} />
                
                <Box sx={{ position: 'relative' }}>
                  <StudentNameForm 
                    onSubmit={handleNameSubmit}
                    isVerification={gameData.activity_type === "verifica"}
                  />
                  
                  {isLoggedIn && isOwner && (
                    <Button
                      variant="contained"
                      startIcon={<ScoreboardIcon />}
                      onClick={handleViewResults}
                      sx={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#218838',
                        },
                        position: 'absolute',
                        right: '16px',
                        bottom: '16px',
                        borderRadius: '20px',
                      }}
                    >
                      Punteggi Salvati
                    </Button>
                  )}
                </Box>
              </>
            ) : (
              <>
                <div className="game-header">
                  <h1>{gameData.title || "Gioco Interattivo"}</h1>
                  <div className="student-info">
                    <PersonIcon className="student-icon" />
                    <span>Studente: <strong>{studentName}</strong></span>
                  </div>
                  <Chip 
                    icon={<AssignmentIcon />} 
                    label={gameData.activity_type === "verifica" ? "Verifica" : "Esercitazione"} 
                    color={gameData.activity_type === "verifica" ? "error" : "primary"}
                    className="activity-tag"
                    sx={{ 
                      fontWeight: 'bold'
                    }} 
                  />
                </div>
                
                <div className="game-header-buttons">
                  <button 
                    className="share-button" 
                    onClick={handleShareGame}
                    aria-label="Condividi"
                    title="Condividi"
                  >
                    <img 
                      src="/interactivegame/share.png" 
                      alt="Condividi" 
                      className="share-icon" 
                    />
                    <span className="share-text">Condividi</span>
                    {showShareFeedback && <span className="share-feedback">URL copiato!</span>}
                  </button>
                </div>
                
                {/* Barra di navigazione tra i giochi - nascosta quando si visualizza il riepilogo */}
                {!showScoreSummary && (
                  <>
                    <GameNavigationBar 
                      chats={gameData.game_data.chats}
                      currentGameIndex={currentGameIndex}
                      completedGames={completedGames}
                      onNavigate={handleNavigateToGame}
                    />
                    
                    {/* Barra di progresso per i giochi */}
                    <ProgressBar 
                      current={currentGameIndex + 1} 
                      total={gameData.game_data.chats.length} 
                      className="game-progress-bar"
                    />
                  </>
                )}
                
                <div className="game-area">
                  {showScoreSummary ? (
                    selectedGameDetails ? (
                      <GameDetails 
                        gameResult={selectedGameDetails.gameResult} 
                        chat={selectedGameDetails.chat}
                        onBackToSummary={handleBackToSummary}
                        onNavigateToGame={handleNavigateToGame}
                      />
                    ) : (
                      gameData.activity_type === "verifica" ? (
                        <VerificationComplete onBackToGames={handleBackToGames} gameData={gameData} completedGames={completedGames} studentName={studentName} />
                      ) : (
                        <ScoreSummary 
                          gameData={gameData}
                          completedGames={completedGames}
                          onShowGameDetails={handleShowGameDetails}
                          onBackToGames={handleBackToGames}
                          onResetGame={handleResetGame}
                          onSaveResults={() => {
                            console.log("======= RICHIESTA DI SALVATAGGIO RISULTATI =======");
                            console.log("completedGames:", completedGames);
                            console.log("Totale giochi completati:", completedGames.length);
                            console.log("Totale giochi disponibili:", gameData.game_data.chats.length);
                            
                            const validGames = completedGames.filter(g => g.completed === true);
                            console.log("Giochi con flag completed=true:", validGames.length);
                            
                            // Verifica che ogni chat abbia un gioco completato
                            const allGamesCompleted = gameData.game_data.chats.every(chat => 
                              completedGames.some(game => game.chatId === chat.id)
                            );
                            console.log("Tutti i giochi hanno una corrispondenza in completedGames:", allGamesCompleted);
                            
                            saveGameResults();
                          }}
                          resultsSaving={resultsSaving}
                          resultsSaved={resultsSaved}
                          isVerification={gameData.activity_type === "verifica"}
                        />
                      )
                    )
                  ) : (
                    <GameContainer 
                      gameData={gameData}
                      currentGameIndex={currentGameIndex}
                      completedGames={completedGames}
                      onGameComplete={handleGameComplete}
                      onSubmitFinalResults={handleSubmitFinalResults}
                    />
                  )}
                </div>
              </>
            )
          ) : (
            <div className="no-data-container">
              <h2>Nessun dato disponibile</h2>
              <p>Non sono stati trovati dati per questo gioco.</p>
              <button onClick={() => navigate('/')}>Torna alla Home</button>
            </div>
          )}
        </motion.div>
      </main>
      
      {/* Controlli Audio - Versione responsive */}
      {isMobile ? (
        <Box sx={{ position: 'fixed', bottom: '15px', right: '15px', zIndex: 1000 }}>
          {/* Pulsante flottante minimalista per dispositivi mobili */}
          <IconButton
            onClick={() => setShowAudioControls(!showAudioControls)}
            sx={{
              backgroundColor: isMuted ? 'rgba(223, 70, 52, 0.8)' : 'rgba(25, 105, 106, 0.8)',
              color: 'white',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: isMuted ? 'rgba(223, 70, 52, 0.9)' : 'rgba(25, 105, 106, 0.9)',
              },
              borderRadius: '50%',
              padding: '10px',
              width: '40px',
              height: '40px'
            }}
          >
            {isMuted ? <MusicOffIcon /> : <MusicNoteIcon />}
          </IconButton>
          
          {/* Pannello espandibile dei controlli per dispositivi mobili */}
          <Fade in={showAudioControls}>
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                bottom: '50px',
                right: '0px',
                zIndex: 1001,
                padding: '10px',
                width: '200px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(5px)',
                display: showAudioControls ? 'block' : 'none',
                borderRadius: '8px',
                border: '1px solid rgba(25, 105, 106, 0.3)'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#19696A', fontSize: '0.75rem' }}>
                  Audio
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setShowAudioControls(false)}
                  sx={{ padding: '2px' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Button
                variant={isMuted ? "outlined" : "contained"} 
                onClick={handleToggleMute}
                size="small"
                fullWidth
                sx={{
                  backgroundColor: isMuted ? 'transparent' : '#19696A',
                  color: isMuted ? '#19696A' : 'white',
                  borderColor: '#19696A',
                  '&:hover': {
                    backgroundColor: isMuted ? 'rgba(25, 105, 106, 0.1)' : '#135758',
                    borderColor: '#135758',
                  },
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  mb: 1
                }}
              >
                {isMuted ? "Attiva musica" : "Disattiva musica"}
              </Button>
              
              {!isMuted && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VolumeOffIcon sx={{ color: '#19696A', fontSize: '1rem' }} />
                  <Slider
                    value={volume}
                    onChange={handleVolumeChange}
                    min={0}
                    max={1}
                    step={0.01}
                    aria-label="Volume"
                    size="small"
                    sx={{
                      color: '#19696A',
                      '& .MuiSlider-thumb': {
                        width: 12,
                        height: 12,
                      },
                      '& .MuiSlider-rail': {
                        opacity: 0.5,
                      }
                    }}
                  />
                  <VolumeUpIcon sx={{ color: '#19696A', fontSize: '1rem' }} />
                </Box>
              )}
            </Paper>
          </Fade>
        </Box>
      ) : (
        /* Versione desktop - controlli fissi più grandi */
        <Box
          sx={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '10px',
            padding: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            backdropFilter: 'blur(5px)',
            border: '1px solid rgba(25, 105, 106, 0.3)'
          }}
        >
          <Typography variant="subtitle2" sx={{ 
            mb: 1, 
            fontWeight: 'bold', 
            color: '#19696A',
            fontSize: '0.75rem'
          }}>
            Controlli Audio
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 1
          }}>
            <Button
              variant={isMuted ? "outlined" : "contained"} 
              startIcon={isMuted ? <MusicOffIcon /> : <MusicNoteIcon />}
              onClick={handleToggleMute}
              size="small"
              sx={{
                backgroundColor: isMuted ? 'transparent' : '#19696A',
                color: isMuted ? '#19696A' : 'white',
                borderColor: '#19696A',
                '&:hover': {
                  backgroundColor: isMuted ? 'rgba(25, 105, 106, 0.1)' : '#135758',
                  borderColor: '#135758',
                },
                width: '100%',
                fontSize: '0.7rem',
                textTransform: 'none'
              }}
            >
              {isMuted ? "Attiva musica" : "Disattiva musica"}
            </Button>
            
            {!isMuted && (
              <Box sx={{ 
                width: '100%', 
                display: 'flex', 
                alignItems: 'center',
                gap: 1
              }}>
                <VolumeOffIcon sx={{ color: '#19696A', fontSize: '1rem' }} />
                <Slider
                  value={volume}
                  onChange={handleVolumeChange}
                  min={0}
                  max={1}
                  step={0.01}
                  aria-label="Volume"
                  sx={{
                    color: '#19696A',
                    '& .MuiSlider-thumb': {
                      width: 14,
                      height: 14,
                    },
                    '& .MuiSlider-rail': {
                      opacity: 0.5,
                    }
                  }}
                />
                <VolumeUpIcon sx={{ color: '#19696A', fontSize: '1rem' }} />
              </Box>
            )}
          </Box>
        </Box>
      )}
      
      {/* Modal di conferma per il reset */}
      <ResetConfirmModal 
        isVisible={showResetConfirm} 
        onConfirm={confirmReset} 
        onCancel={cancelReset} 
      />
      
      {/* Snackbar per le notifiche */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default InteractiveGamePage;

// Funzione helper per il colore del punteggio
export const getScoreColor = (score) => {
  if (score >= 80) return '#19696A';
  if (score >= 60) return '#FF9800';
  return '#df4634';
};

